import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, TrendingUp, Shield, Users } from "lucide-react";
const Hero = () => {
  const [loanAmount, setLoanAmount] = useState<string>("");
  const [loanPeriod, setLoanPeriod] = useState<string>("12");
  const [loanType, setLoanType] = useState<string>("normal");
  const [customInterestRate, setCustomInterestRate] = useState<string>("12");
  const [monthlyPayment, setMonthlyPayment] = useState<number | null>(null);
  const [totalPayment, setTotalPayment] = useState<number | null>(null);
  const [totalInterest, setTotalInterest] = useState<number | null>(null);

  // Loan products with their specific rates and terms
  const loanProducts = {
    normal: {
      name: "Development Loan",
      interestRate: 12,
      // 1% per month = 12% p.a.
      maxPeriod: 96,
      description: "Up to 3x your savings limit for 96 months"
    },
    emergency: {
      name: "Emergency Loan",
      interestRate: 18,
      // 1.5% per month = 18% p.a.
      maxPeriod: 12,
      description: "For unexpected urgent needs"
    },
    education: {
      name: "Education Loan",
      interestRate: 15,
      // 1.25% per month = 15% p.a.
      maxPeriod: 12,
      description: "For school and university fees"
    },
    device: {
      name: "Salary Advance",
      interestRate: 24,
      // 2% per month = 24% p.a.
      maxPeriod: 3,
      description: "Up to KSh 150,000 for 3 months at 2% p.m."
    },
    custom: {
      name: "Custom Calculator",
      interestRate: 12,
      maxPeriod: 198,
      description: "Set your own interest rate and terms"
    }
  };

  // Generate month options based on selected loan type
  const generateMonthOptions = () => {
    const maxMonths = loanProducts[loanType as keyof typeof loanProducts].maxPeriod;
    const options = [];

    // Add common short-term options
    for (let i = 1; i <= Math.min(12, maxMonths); i++) {
      options.push(i);
    }

    // Add yearly increments up to the max
    for (let i = 18; i <= maxMonths; i += 6) {
      if (i <= maxMonths) {
        options.push(i);
      }
    }
    return options;
  };
  const calculateMonthlyPayment = () => {
    const principal = parseFloat(loanAmount);
    const months = parseInt(loanPeriod);
    const selectedProduct = loanProducts[loanType as keyof typeof loanProducts];

    // Use custom interest rate for custom calculator, otherwise use product rate
    let annualRate = selectedProduct.interestRate;
    if (loanType === "custom") {
      const customRate = parseFloat(customInterestRate);
      if (customRate > 0 && customRate <= 50) {
        annualRate = customRate;
      }
    }
    if (!principal || !months || !annualRate || principal <= 0 || months <= 0 || annualRate < 0) {
      setMonthlyPayment(null);
      setTotalPayment(null);
      setTotalInterest(null);
      return;
    }

    // Convert annual rate to monthly rate
    const monthlyRate = annualRate / 100 / 12;

    // Calculate monthly payment using loan formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    const total = payment * months;
    const interest = total - principal;
    setMonthlyPayment(payment);
    setTotalPayment(total);
    setTotalInterest(interest);
  };

  // Auto-calculate when loan type changes
  const handleLoanTypeChange = (value: string) => {
    setLoanType(value);
    const selectedProduct = loanProducts[value as keyof typeof loanProducts];

    // Reset period if it exceeds the new product's max period
    if (parseInt(loanPeriod) > selectedProduct.maxPeriod) {
      setLoanPeriod("12");
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('KES', 'KSh');
  };
  return <section className="relative min-h-[80vh] flex items-center bg-gradient-hero" aria-label="Hero section">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,.1)_50%,transparent_75%,transparent_100%)] bg-[length:250px_250px] animate-pulse opacity-20" aria-hidden="true"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Your Partner in{" "}
                <span className="text-accent bg-gradient-to-r from-accent to-warning-orange bg-clip-text text-transparent">
                  Prosperity
                </span>
              </h1>
              <p className="text-xl text-white/90 max-w-lg">Join our growing community of 280 members who trust Natcon Sacco for their financial growth. Experience modern banking with a community touch.</p>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">280</div>
                <div className="text-xs sm:text-sm text-white/80">Happy Members</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">KSh 117.3M</div>
                <div className="text-xs sm:text-sm text-white/80">Assets Under Management</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-white">10</div>
                <div className="text-xs sm:text-sm text-white/80">Years of Service</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" asChild>
                <a href="/membership-application" aria-label="Join Natcon Sacco - Become a member">
                  Join Natcon Today
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm" asChild>
                <a href="https://selfservice.natconsacco.or.ke/" target="_blank" rel="noopener noreferrer" aria-label="Apply for loan - Opens in new window">Apply for Loan</a>
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 pt-4" role="list" aria-label="Trust indicators">
              <div className="flex items-center gap-2 text-white/80" role="listitem">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                <span className="text-xs sm:text-sm">Fully Licensed & Regulated</span>
              </div>
              <div className="flex items-center gap-2 text-white/80" role="listitem">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                <span className="text-xs sm:text-sm">20% Annual Dividend</span>
              </div>
            </div>
          </div>

          {/* Right Column - Loan Calculator */}
          <div className="lg:flex justify-end">
            <Card className="w-full max-w-md shadow-strong bg-card/95 backdrop-blur" data-calculator role="region" aria-label="Loan calculator">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-gradient-primary flex items-center justify-center" aria-hidden="true">
                    <Calculator className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg">Loan Calculator</h2>
                    <p className="text-sm text-muted-foreground">Estimate your monthly payments</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">
                      Loan Type
                    </label>
                    <Select value={loanType} onValueChange={handleLoanTypeChange}>
                      <SelectTrigger className="w-full" aria-label="Select loan type">
                        <SelectValue placeholder="Select loan type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Development Loan - 12% p.a.</SelectItem>
                        <SelectItem value="emergency">Emergency Loan - 18% p.a.</SelectItem>
                        <SelectItem value="education">Education Loan - 15% p.a.</SelectItem>
                        <SelectItem value="device">Salary Advance - 2% p.m.</SelectItem>
                        <SelectItem value="custom">Custom Calculator</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {loanProducts[loanType as keyof typeof loanProducts].description}
                    </p>
                  </div>

                  {loanType === "custom" && <div>
                      <label htmlFor="custom-rate" className="text-sm font-medium text-foreground block mb-2">
                        Interest Rate (% per annum)
                      </label>
                      <input id="custom-rate" type="number" placeholder="12" min="0.1" max="50" step="0.1" value={customInterestRate} onChange={e => setCustomInterestRate(e.target.value)} className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" aria-describedby="custom-rate-hint" />
                      <p id="custom-rate-hint" className="text-xs text-muted-foreground mt-1">
                        Maximum rate: 50% per annum
                      </p>
                    </div>}

                  <div>
                    <label htmlFor="loan-amount" className="text-sm font-medium text-foreground block mb-2">
                      Loan Amount (KSh)
                    </label>
                    <input id="loan-amount" type="number" placeholder="500,000" value={loanAmount} onChange={e => setLoanAmount(e.target.value)} className="w-full p-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background" aria-describedby="loan-amount-hint" />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">
                      Loan Period (Months)
                    </label>
                    <Select value={loanPeriod} onValueChange={setLoanPeriod}>
                      <SelectTrigger className="w-full" aria-label="Select loan period">
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        {generateMonthOptions().map(months => <SelectItem key={months} value={months.toString()}>
                            {months} month{months > 1 ? 's' : ''} {months >= 12 ? `(${(months / 12).toFixed(1)} year${months >= 24 ? 's' : ''})` : ''}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Max period: {loanProducts[loanType as keyof typeof loanProducts].maxPeriod} months
                    </p>
                  </div>

                  <Button onClick={calculateMonthlyPayment} className="w-full bg-gradient-primary hover:opacity-90">
                    Calculate Payment
                  </Button>

                  <div className="bg-secondary/50 p-4 rounded-lg space-y-3">
                    <div className="text-center">
                      <div className="text-sm text-muted-foreground">Monthly Payment</div>
                      <div className="text-2xl font-bold text-primary">
                        {monthlyPayment ? formatCurrency(monthlyPayment) : "Enter loan details"}
                      </div>
                    </div>
                    
                    {totalPayment && totalInterest && <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border/50">
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground">Total Payment</div>
                          <div className="text-sm font-semibold">{formatCurrency(totalPayment)}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground">Total Interest</div>
                          <div className="text-sm font-semibold">{formatCurrency(totalInterest)}</div>
                        </div>
                      </div>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
};
export default Hero;